<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    class="h-[90%]"
    position="bottom"
  >
    <div class="h-full flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b">
        <h3 class="text-lg font-medium">
          {{ type === "avatar" ? "编辑头像" : "编辑立绘" }}
        </h3>
        <button
          @click="emit('update:show', false)"
          class="btn btn-ghost btn-sm btn-square"
        >
          <i class="iconify mingcute--close-line"></i>
        </button>
      </div>

      <!-- Method Selection -->
      <div class="p-4 border-b">
        <div class="flex gap-4">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              :value="'upload'"
              v-model="selectedMethod"
              class="radio radio-primary"
            />
            <span>{{
              type === "avatar" ? "上传自定义头像" : "上传自定义立绘"
            }}</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              :value="'generate'"
              v-model="selectedMethod"
              class="radio radio-primary"
            />
            <span>{{ type === "avatar" ? "生成头像" : "生成立绘" }}</span>
          </label>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto">
        <!-- Upload Method -->
        <div v-if="selectedMethod === 'upload'" class="p-4">
          <div class="flex flex-col items-center justify-center h-full gap-4">
            <button
              class="btn btn-primary btn-wide btn-sm"
              @click="emit('upload'), emit('update:show', false)"
            >
              <i class="iconify mingcute--upload-line mr-2"></i>
              选择图片
            </button>
          </div>
        </div>

        <!-- Generate Method -->
        <div v-else class="p-4 flex flex-col gap-4 min-h-[312px]">
          <!-- Step 1: Image Selection -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <div
                class="w-6 h-6 rounded-full bg-neutral text-neutral-content text-xs flex items-center justify-center"
              >
                1
              </div>
              <span class="font-medium">选择要参考的内容</span>
            </div>

            <div class="flex gap-2">
              <button
                :class="[
                  'btn btn-sm flex-1 ',
                  selectedImageType === 'text' ? 'btn-primary' : '',
                ]"
                @click="selectedImageType = 'text'"
              >
                文字描述
              </button>
              <button
                :class="[
                  'btn btn-sm flex-1 ',
                  selectedImageType === 'pixel' ? 'btn-primary' : '',
                ]"
                :disabled="!mateSprite.imageMediaId"
                @click="selectedImageType = 'pixel'"
              >
                像素小人
              </button>
              <button
                :class="[
                  'btn btn-sm flex-1 ',
                  selectedImageType === 'reference' ? 'btn-primary' : '',
                ]"
                :disabled="!mateSprite.referenceMediaId"
                @click="selectedImageType = 'reference'"
              >
                参考图
              </button>
            </div>

            <!-- Image Preview -->
            <div
              v-if="
                selectedImageType === 'pixel' ||
                selectedImageType === 'reference'
              "
              class="flex justify-center items-center"
            >
              <div class="h-20 rounded-lg overflow-hidden">
                <img
                  v-if="selectedImageType === 'pixel' && mateSprite.imageMedia"
                  :src="mateSprite.imageMedia.url"
                  alt="像素小人预览"
                  class="w-full h-full object-contain"
                  crossorigin="anonymous"
                />
                <img
                  v-else-if="
                    selectedImageType === 'reference' &&
                    mateSprite.referenceMedia
                  "
                  :src="mateSprite.referenceMedia.url"
                  alt="参考图预览"
                  class="w-full h-full object-cover"
                  crossorigin="anonymous"
                />
              </div>

              <div>
                <button
                  v-if="selectedImageType === 'reference'"
                  @click="clickChangeReference"
                  class="btn btn-xs"
                >
                  更换参考图
                </button>

                <button
                  v-if="selectedImageType === 'pixel'"
                  @click="clickChangePixel"
                  class="btn btn-xs"
                >
                  编辑像素小人
                </button>
              </div>
            </div>
          </div>

          <!-- Step 2: Description Input -->
          <div class="space-y-3" v-if="selectedImageType !== 'pixel'">
            <div class="flex items-center gap-2">
              <div
                class="w-6 h-6 rounded-full bg-neutral text-neutral-content text-xs flex items-center justify-center"
              >
                2
              </div>
              <span class="font-medium">
                {{
                  selectedImageType === "text"
                    ? "填写图片描述（必填）"
                    : "填写图片补充描述（可选）"
                }}
              </span>
            </div>
            <van-field
              v-model="description"
              type="textarea"
              placeholder="请输入对图片的描述..."
              :rows="3"
              autosize
            />
          </div>

          <!-- Step 3: Generation Button -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <div
                class="w-6 h-6 rounded-full bg-neutral text-neutral-content text-xs flex items-center justify-center"
              >
                {{ selectedImageType === "pixel" ? 2 : 3 }}
              </div>
              <span class="font-medium">开始生成</span>
            </div>

            <div class="flex gap-2">
              <button
                v-if="
                  (type === 'avatar' && !mateSprite.avatarMediaId) ||
                  (type === 'portrait' && !mateSprite.portraitMediaId)
                "
                class="btn btn-primary flex-1"
                @click="handleGenerate(true)"
                :disabled="selectedImageType === 'text' && !description"
              >
                <div class="flex flex-col">
                  <div class="flex items-center gap-0.5">
                    立即生成 <PowerIcon /> 2
                  </div>
                </div>
              </button>
              <template v-else>
                <button
                  class="btn btn-outline flex-1"
                  @click="handleGenerate(true)"
                  :disabled="selectedImageType === 'text' && !description"
                >
                  <div class="flex flex-col">
                    <div class="flex items-center gap-0.5">
                      覆盖并生成 <PowerIcon /> 2
                    </div>
                    <span class="text-xs opacity-75">覆盖当前形象</span>
                  </div>
                </button>
                <button
                  class="btn btn-primary flex-1"
                  @click="handleGenerate(false)"
                  :disabled="selectedImageType === 'text' && !description"
                >
                  <div class="flex flex-col">
                    <div class="flex items-center gap-0.5">
                      创建并生成 <PowerIcon /> 2
                    </div>
                    <span class="text-xs opacity-75">保留当前形象</span>
                  </div>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VanPopup>
</template>

<script lang="ts" setup>
import { MateSprite } from "@/types/game";
import { ref } from "vue";

const props = withDefaults(
  defineProps<{
    show: boolean;
    type?: "avatar" | "portrait";
    mateSprite: MateSprite;
  }>(),
  {
    type: "avatar",
  }
);

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (
    e: "generate",
    value: "text" | "reference" | "pixel",

    overwrite: boolean,
    prompt?: string
  ): void;
  (e: "upload"): void;
  (e: "changeReference"): void;
  (e: "changePixel"): void;
}>();

const selectedMethod = ref<"upload" | "generate">("upload");
const selectedImageType = ref<"pixel" | "reference" | "text">("text");
const description = ref("");

const handleGenerate = (overwrite: boolean) => {
  emit("generate", selectedImageType.value, overwrite, description.value);
  emit("update:show", false);
};

const clickChangeReference = () => {
  emit("changeReference");
};

const clickChangePixel = () => {
  emit("changePixel");
};
</script>
